import { findOverseasRealEstateBusinessContentId, OverseasRealEstateBusinessContentStrs, OverseasRealEstateBusinessContentText } from 'views/utils/enums';
import { useFormik } from 'formik';
import { SelectorProps } from 'views/templates/module/selector';
import { InputTemplateProps } from 'views/templates/module/inputTemplate';
import { useAnswerRecorderMutation } from 'views/utils/graphql/graphql';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { useGlobalLoadingHandler } from 'views/mutual/globalLoading/hook';
import * as Yup from 'yup';
import { AnswerRecorderType } from '../hooks';
import { OverseasFatcaResponseSelectionProps } from './fatcaType';

// ① 会社区分（日本：金融機関/一般法人/いずれでもない）
export enum JpEntityClassification {
    JapanFinancialInstitution = 1,
    JapanCorporationGeneral,
    Other
}
  
// ② NFFE 区分（FATCA）
export enum NffeClassification {
    ActiveNffe  = 1,
    PassiveNffe,
}

export type OverseasRealEstateFatcaType = {
    businessContent: string;
    businessContentOtherText: string;    
    jpEntityClassification: JpEntityClassification | null;
    nffeClassification: NffeClassification | null;
}

type GetClassificationResult = {
    jpEntityClassification: JpEntityClassification | null;
    nffeClassification: NffeClassification | null;    
}

//(0)金融業
const P1_SET = new Set<string>([
    OverseasRealEstateBusinessContentText.Kinyugyo,
]);

//(6)不動産業・物品賃貸業
//(13)その他
const PASSIVE_P2_SET = new Set<string>([
    OverseasRealEstateBusinessContentText.FudousanBuppinChintaiGyo,
    OverseasRealEstateBusinessContentText.Sonohoka,
]);

type Classification = GetClassificationResult;

const getClassification = (businessContent?: string): Classification | null => {
    if(businessContent == null || businessContent === '') return null;

    //（０）金融業を選択した場合
  if (P1_SET.has(businessContent)) {
    return {
      jpEntityClassification: JpEntityClassification.JapanFinancialInstitution,
      nffeClassification: null,
    };
  }

  //（６）不動産業・（１３）その他を選択した場合
  if (PASSIVE_P2_SET.has(businessContent)) {
    return {
      jpEntityClassification: JpEntityClassification.JapanCorporationGeneral,
      nffeClassification: NffeClassification.PassiveNffe,
    };
  }

  //上記以外を選択した場合
  return {
    jpEntityClassification: JpEntityClassification.JapanCorporationGeneral,
    nffeClassification: NffeClassification.ActiveNffe,
  };
};
const MISMATCH_MESSAGE = '事前に入力いただいた業種と回答が不一致のため、この回答は選択できません。';
const FACE_TO_FACE_MESSAGE = '対面でのお手続きが必要です。恐れ入りますが最寄りの営業店もしくは右上お問い合わせからご連絡をお願いします。';

const isFatcaAnswerValid = (
    businessContent: string,
    jpEntityClassification: JpEntityClassification | null,
    nffeClassification: NffeClassification | null
  ): boolean => {
    const expected = getClassification(businessContent);

    //無回答の場合は回答不可
    if(expected == null || (jpEntityClassification === null && nffeClassification === null)){
        return false;
    }
  
    return (
      expected.jpEntityClassification === jpEntityClassification &&
      expected.nffeClassification === nffeClassification
    );
  };

export const useOverseasRealEstateResponse = (navigate: (isError?: boolean) => void) => {
    const [_, answerRecorder] = useAnswerRecorderMutation();
    const { showError, messageClose } = useGlobalToastHandler();

    const { showLoading, hideLoading } = useGlobalLoadingHandler();

    const validation = Yup.object().shape({
        businessContentOtherText: Yup.string().when('businessContent', {
            is: (val: string) => val === OverseasRealEstateBusinessContentText.Sonohoka,
            then: (schema) => schema.required('入力してください').max(100, '最大100文字で入力してください'),
            otherwise: (schema) => schema.strip(),
          }),
        jpEntityClassification:Yup.number()
        .when('businessContent', {
            is: (val: string) => val != null,
            then: (schema: Yup.NumberSchema): Yup.NumberSchema => {
                return schema.test('jpEntityClassification', FACE_TO_FACE_MESSAGE, function(value) {
                const businessContent = this.parent.businessContent as string;
                //（２）は子要素があるため、選択された時点ではエラーを出さない
                if (businessContent == null || value == null || value === JpEntityClassification.JapanCorporationGeneral) return true;
                const expectedClassification = getClassification(businessContent)!.jpEntityClassification;
                if (value === expectedClassification) return true;
                //（１）日本国内金融機関を選択した場合、または業種が（０）金融業の場合は業種不一致のメッセージを表示する
                if (value === JpEntityClassification.JapanFinancialInstitution || P1_SET.has(businessContent)) {
                    return this.createError({ message: MISMATCH_MESSAGE });
                }
                return false;
                });
            },
            otherwise: (schema) => schema.strip(),
        }).nullable(),

        nffeClassification: Yup.number()
            .when('jpEntityClassification', {
            is: (val: JpEntityClassification) => val === JpEntityClassification.JapanCorporationGeneral,
            then: (schema: Yup.NumberSchema): Yup.NumberSchema => {
                return schema.test('nffeClassification', FACE_TO_FACE_MESSAGE, function(value) {
                const businessContent = this.parent.businessContent as string;
                if (businessContent == null || value == null) return true;
                const expectedClassification = getClassification(businessContent)!.nffeClassification;
                if (value === expectedClassification) return true;
                //業種が（０）金融業の場合は業種不一致のメッセージを表示する
                if (P1_SET.has(businessContent)) {
                    return this.createError({ message: MISMATCH_MESSAGE });
                }
                return false;
                });
            },
            otherwise: (schema) => schema.strip(),
        }).nullable(),
      });

    const initialValues: OverseasRealEstateFatcaType = {
        businessContent: '',
        businessContentOtherText: '',

        jpEntityClassification: null,
        nffeClassification: null,
    };

    const formik = useFormik<OverseasRealEstateFatcaType>({
        enableReinitialize: false,
        initialValues,
        async onSubmit(input) {
            showLoading();
            const res = await answerRecorder({
                input: {
                    BusinessContent: findOverseasRealEstateBusinessContentId(input.businessContent),
                    BusinessContentOther: input.businessContentOtherText ?? null,
                    Fatca: input.jpEntityClassification,
                    Nffe: input.nffeClassification,
                    Type: AnswerRecorderType.Fatca,
                }
            });
            hideLoading();
            if (!res?.data) return;
            if (res.data.AnswerRecorder?.Code !== 0) {
                showError(res.data.AnswerRecorder?.Message);
            }

            const showErrorPage = input.businessContent === OverseasRealEstateBusinessContentText.Sonohoka || input.businessContent === OverseasRealEstateBusinessContentText.FudousanBuppinChintaiGyo;    
            navigate(showErrorPage);            
        },
        validationSchema: validation,
        validateOnChange: true,
        validateOnBlur: false,
        validate: () => {
            // バリデーションかける時に必ずエラートーストを閉じる            
            messageClose();
        },
    });

    const businessContentSelectorProps: SelectorProps = {
        options: OverseasRealEstateBusinessContentStrs,
        selectedOption: formik.values.businessContent,
        setSelectedOption: async (value: string) => {
            await formik.setFieldValue('businessContent', value);
            await formik.setFieldValue('businessContentOtherText', '');
        },
        error: formik.errors.businessContent,    
    };

    const businessContentOtherTextProps: InputTemplateProps | null = formik.values.businessContent === OverseasRealEstateBusinessContentText.Sonohoka ? {
        value: formik.values.businessContentOtherText,
        onChange: async (value: string) => await formik.setFieldValue('businessContentOtherText', value),
        error: formik.touched.businessContentOtherText ? formik.errors.businessContentOtherText : '',
    } : null;


    const selectionProps : OverseasFatcaResponseSelectionProps = {
        jpEntityClassificationSelectionsProps: {
            selected: formik.values.jpEntityClassification,
            onChange: async (value: JpEntityClassification) => {
                await formik.setFieldValue('jpEntityClassification', value)
                await formik.setFieldValue('nffeClassification', null)
            },
            error: formik.errors.jpEntityClassification,
        },
        nffeClassificationSelectionsProps: {
            selected: formik.values.nffeClassification,
            onChange: async(value: NffeClassification) => {
                await formik.setFieldValue('nffeClassification', value)
            },
            error: formik.errors.nffeClassification,
        },
    }

    return{
        formik,
        businessContentProps : {
            businessContentSelectorProps,
            businessContentOtherTextProps
        },
        selectionProps,
        isFatcaAnswerValid : isFatcaAnswerValid(formik.values.businessContent, formik.values.jpEntityClassification, formik.values.nffeClassification),
    }
};
